<template>
  <div class="d-flex ai-moderation-wrapper">
    <!-- navbar: left sidebar for admin layout -->
    <navigator :flag="navFlag" />

    <!-- header + content + footer -->
    <div class="ai-agent-page">
      <div class="ai-agent-header">
        <h2 class="page-title">AI Agent Management</h2>

        <div class="moderation-toggle-wrapper">
          <span class="toggle-label">AI Moderation</span>

          <!-- Custom red toggle; logic handled in onModerationToggle hook -->
          <label class="toggle-switch">
            <!-- hook: bind global moderation on/off to backend -->
            <input
              type="checkbox"
              v-model="isModerationEnabled"
              @change="onModerationToggle"
            />
            <span class="toggle-track">
              <span class="toggle-knob"></span>
            </span>
          </label>
        </div>
      </div>

      <!-- card & page footer -->
      <div class="ai-agent-content">
        <div
          class="agent-card-grid"
          :class="{ 'content-disabled': !isModerationEnabled }"
        >
          <!-- hook: render list from backend result -->
          <div
            v-for="agent in agents"
            :key="agent.id"
            class="agent-card"
          >
            <!-- Card header: status circle + name + Edit / Delete -->
            <div class="agent-card-header">
              <!-- Status circle: click to mark this agent as active -->
              <button
                type="button"
                class="status-dot"
                :class="{ active: agent.id === activeAgentId }"
                @click="onSelectAgent(agent)"
              ></button>

              <h3 class="agent-name">
                {{ agent.name }}
              </h3>

              <div class="agent-actions">
                <button
                  type="button"
                  class="btn-pill btn-edit"
                  @click="openEditModal(agent)"
                >
                  Edit
                  <!-- hook: open edit popup with current agent data -->
                </button>
                <button
                  type="button"
                  class="btn-pill btn-delete"
                  @click="openDeleteConfirm(agent)"
                >
                  Delete
                  <!-- hook: ask backend to delete this agent -->
                </button>
              </div>
            </div>

            <!-- Flags -->
            <div class="agent-section">
              <div class="section-label">Flags:</div>
              <div class="flag-chips">
                <span
                  v-for="(flag, idx) in agent.flags"
                  :key="flag + idx"
                  class="chip chip--flag"
                >
                  {{ flag }}
                </span>
              </div>
            </div>

            <!-- Profile & Age Group -->
            <div class="agent-meta">
              <div class="agent-section">
                <div class="section-label">Profile</div>
                <span class="chip chip--meta">
                  {{ agent.profile }}
                </span>
              </div>

              <div class="agent-section">
                <div class="section-label">Age Group</div>
                <span class="chip chip--meta">
                  {{ agent.ageGroup }}
                </span>
              </div>
            </div>

            <!-- statics -->
            <div class="agent-stats-row">
              <div class="stat-pill">
                <div class="stat-label">Survey flagged</div>
                <div class="stat-value">{{ agent.surveyFlagged }}</div>
              </div>
              <div class="stat-pill">
                <div class="stat-label">API Cost</div>
                <div class="stat-value">{{ agent.apiCost }}</div>
              </div>
              <div class="stat-pill">
                <div class="stat-label">Total Usage</div>
                <div class="stat-value">{{ agent.totalUsage }}</div>
              </div>
            </div>
          </div>
        </div>

        <!-- footer -->
        <div
          class="agent-footer"
          :class="{ 'content-disabled': !isModerationEnabled }"
        >
          <button
            type="button"
            class="footer-btn footer-btn-back"
            @click="onBack"
          >
            Back
            <!-- hook: go back to previous admin page -->
          </button>

          <div class="pagination">
            <!-- hook: real pagination if agents are paged by backend -->
            <button type="button" class="page-pill">1</button>
          </div>

          <button
            type="button"
            class="footer-btn footer-btn-add"
            @click="openAddModal"
          >
            Add
            <!-- hook: open add-agent modal / route -->
          </button>
        </div>
      </div>
    </div>

    <b-modal
      v-model="showEditModal"
      hide-footer
      centered
      size="lg"
    >
      <template #modal-header="{ close }">
        <h5 class="modal-title">
          {{ isAddMode ? 'Add AI Agent' : 'Edit AI Agent' }}
        </h5>
        <b-button variant="outline-danger" size="sm" @click="close">X</b-button>
      </template>

      <div class="edit-modal-body">
        <div class="edit-section">
          <label class="edit-label">AI Name</label>
          <input
            v-model="editForm.name"
            type="text"
            class="edit-input"
            placeholder="Enter AI name"
          />
        </div>

        <!-- Flags -->        
        <div class="edit-section">
          <label class="edit-label">Flags</label>

          <div class="edit-flag-list">
            <span
              v-for="(flag, idx) in editForm.flags"
              :key="flag + idx"
              class="edit-flag-pill"
            >
              {{ flag }}
              <button
                type="button"
                class="edit-flag-remove"
                @click="removeFlag(idx)"
              >
                ×
              </button>
            </span>

            <button
              type="button"
              class="edit-flag-add-pill"
              @click="openFlagInput"
            >
              +
            </button>
          </div>

          <!-- Input row only appears when showFlagInput is true -->
          <div
            v-if="showFlagInput"
            class="edit-flag-input-row"
          >
            <input
              v-model="newFlagText"
              type="text"
              class="edit-input"
              placeholder="Type a flag, e.g. 'Violence'"
              ref="flagInput"
            />
            <button
              type="button"
              class="small-btn"
              :disabled="!newFlagText.trim()"
              @click="addFlag"
            >
              Add
            </button>
          </div>
        </div>

        <!-- Profile -->
        <div class="edit-section">
          <label class="edit-label">Profile</label>
          <select v-model="editForm.profile" class="edit-input">
            <option value="Balanced">Balanced</option>
            <option value="Strict">Strict</option>
            <option value="Custom">Custom</option>
          </select>
        </div>

        <!-- Age Group -->
        <div class="edit-section">
          <label class="edit-label">Age Group</label>
          <select v-model="editForm.ageGroup" class="edit-input">
            <option value="13+">13+</option>
            <option value="16+">16+</option>
            <option value="18+">18+</option>
          </select>
        </div>

        <!-- Additional Prompt -->
        <div class="edit-section">
          <label class="edit-label">Additional User Prompt</label>
          <textarea
            v-model="editForm.additionalPrompt"
            class="edit-textarea"
            rows="3"
            placeholder="Optional system / user prompt"
          ></textarea>
        </div>

        <!-- API Key -->
        <div class="edit-section">
          <label class="edit-label">API Key</label>
          <input
            v-model="editForm.apiKey"
            type="text"
            class="edit-input"
            :placeholder="isAddMode ? 'Paste your Gemini API key' : 'Leave blank to keep existing key'"
          />
          <small v-if="isAddMode" style="color: #666; font-size: 12px;">
            Get your free API key from: https://aistudio.google.com/app/apikey
          </small>
        </div>

        <!-- API Cost -->
        <div class="edit-section">
          <label class="edit-label">API Cost (CAD)</label>
          <input
            v-model="editForm.apiCost"
            type="text"
            class="edit-input"
            placeholder="$0.0001 per call"
          />
        </div>

        <div class="edit-modal-footer">
          <button
            type="button"
            class="modal-btn modal-btn-secondary"
            @click="cancelEdit"
          >
            Cancel
          </button>
          <button
            type="button"
            class="modal-btn modal-btn-primary"
            @click="onSaveClicked"
          >
            Save
          </button>
        </div>
      </div>
    </b-modal>

    <!-- save confirmation popup -->
    <b-modal
      v-model="showSaveConfirmModal"
      hide-footer
      centered
    >
      <template #modal-header="{ close }">
        <h5 class="modal-title">Confirm changes</h5>
        <b-button variant="outline-danger" size="sm" @click="close">X</b-button>
      </template>

      <p class="confirm-text">
        Are you sure you want to save changes for
        <strong>{{ editForm.name || 'this AI agent' }}</strong>?
      </p>

      <div class="confirm-footer">
        <button
          type="button"
          class="modal-btn modal-btn-secondary"
          @click="showSaveConfirmModal = false"
        >
          Cancel
        </button>
        <button
          type="button"
          class="modal-btn modal-btn-primary"
          @click="confirmSaveAgent"
        >
          Save
        </button>
      </div>
    </b-modal>

    <!-- Delete confirmation popup -->
    <b-modal
      v-model="showDeleteConfirmModal"
      hide-footer
      centered
    >
      <template #modal-header="{ close }">
        <h5 class="modal-title">Delete agent</h5>
        <b-button variant="outline-danger" size="sm" @click="close">X</b-button>
      </template>

      <p class="confirm-text">
        Are you sure you want to delete
        <strong>{{ agentToDelete ? agentToDelete.name : 'this AI agent' }}</strong>?
      </p>

      <div class="confirm-footer">
        <button
          type="button"
          class="modal-btn modal-btn-secondary"
          @click="showDeleteConfirmModal = false"
        >
          Cancel
        </button>
        <button
          type="button"
          class="modal-btn modal-btn-primary"
          @click="confirmDeleteAgent"
        >
          Delete
        </button>
      </div>
    </b-modal>
  </div>
</template>

<script>
// navbar: shared sidebar for admin area
import navigator from '../layout/NavBarNew.vue'
import { saveAgentSettings, getAgentSettings, deleteAgentSettings } from '../../GeminiService'

export default {
  name: 'AiModeration',
  components: {
    navigator
  },
  data () {
    return {
      // navbar: which item NavBar should highlight
      navFlag: 2,

      // Whether global AI moderation is enabled (dafault: true)
      isModerationEnabled: true,

      // Which agent is currently active (red circle)
      activeAgentId: null,

      // Client ID (temporary: using 'default-client' until we implement Client collection)
      clientId: 'default-client',

      // Edit / add modal state
      showEditModal: false,
      isAddMode: false, // true = Add, false = Edit

      // Confirm modals for Save / Delete
      showSaveConfirmModal: false,
      showDeleteConfirmModal: false,

      // Form model used for both edit and add
      editForm: {
        id: null,
        name: '',
        flags: [],
        profile: 'Balanced',
        ageGroup: '13+',
        additionalPrompt: '',
        apiKey: '',
        apiCost: '',
        surveyFlagged: '',
        totalUsage: ''
      },

      // Temp store for the agent to delete
      agentToDelete: null,

      // New flag text being typed
      newFlagText: '',

      // Whether to show flag input row (opened by orange plus pill)
      showFlagInput: false,

      // Agent list - loaded from backend in mounted()
      agents: []
    }
  },
  async mounted() {
    // Load agent from client-side service
    try {
      const userId = window.localStorage.getItem('userId');
      const result = await getAgentSettings(userId || this.clientId);
      if (result.success && result.data) {
        const agent = result.data;

        // Load is_enabled status
        this.isModerationEnabled = agent.is_enabled !== false; // Default true

        this.agents = [{
          id: userId || this.clientId,
          name: 'My AI Agent',
          flags: agent.custom_flags || [],
          profile: agent.sensitivity_profile || 'Balanced',
          ageGroup: agent.age_group || '13+',
          surveyFlagged: (agent.surveys_flagged || 0).toString(),
          apiCost: 'Free (Gemini)',
          totalUsage: (agent.usage_count || 0).toString(),
          additionalPrompt: agent.additional_instructions || '',
          apiKey: agent.api_key || '' // Load API key (will show as dots in password field)
        }];
      } else {
        // No agent configured yet - show empty state
        this.agents = [];
        this.isModerationEnabled = true; // Default enabled
      }
    } catch (error) {
      console.error('Failed to load agent:', error);
      // Keep empty if load fails
      this.agents = [];
    }
  },
  methods: {

    //AI MOderation toggle
    // Fired when moderation toggle is changed
    async onModerationToggle () {
      try {
        const userId = window.localStorage.getItem('userId') || this.clientId;

        // Save toggle state to Firestore
        await saveAgentSettings(
          userId,
          '', // Keep existing API key
          '', // Keep existing profile
          [], // Keep existing flags
          '', // Keep existing age group
          '', // Keep existing instructions
          this.isModerationEnabled // Update enabled status
        );

        console.log('✅ AI moderation', this.isModerationEnabled ? 'ENABLED' : 'DISABLED');

        this.$bvToast.toast(
          this.isModerationEnabled ? '✅ AI Moderation Enabled' : '⚠️ AI Moderation Disabled',
          {
            title: 'Settings Updated',
            variant: this.isModerationEnabled ? 'success' : 'warning',
            solid: true
          }
        );
      } catch (error) {
        console.error('Failed to update toggle:', error);
        // Revert toggle if save failed
        this.isModerationEnabled = !this.isModerationEnabled;
      }
    },

    // Select an agent: red circle; backend can own this later
    onSelectAgent (agent) {
      this.activeAgentId = agent.id
      // hook: notify backend which agent is active
      console.log('Active agent selected:', agent.name)
    },

    // Go back to previous page
    onBack () {
      // hook: if there is a specific route, use $router.push instead
      this.$router.back()
    },

    // Open edit modal and populate form
    openEditModal (agent) {
      this.isAddMode = false
      this.fillEditForm(agent)
      this.showEditModal = true
    },

    // Open add modal with empty form
    openAddModal () {
      this.isAddMode = true
      const empty = {
        id: null,
        name: '',
        flags: [],
        profile: 'Balanced',
        ageGroup: '13+',
        additionalPrompt: '',
        apiKey: '',
        apiCost: '',
        surveyFlagged: '',
        totalUsage: ''
      }
      this.fillEditForm(empty)
      this.showEditModal = true
    },

    // Deep-copy agent into editForm
    fillEditForm (agent) {
      this.editForm = {
        id: agent.id,
        name: agent.name || '',
        flags: Array.isArray(agent.flags) ? [...agent.flags] : [],
        profile: agent.profile || 'Balanced',
        ageGroup: agent.ageGroup || '13+',
        additionalPrompt: agent.additionalPrompt || '',
        apiKey: agent.apiKey || '',
        apiCost: agent.apiCost || '',
        surveyFlagged: agent.surveyFlagged || '',
        totalUsage: agent.totalUsage || ''
      }
      this.newFlagText = ''
      this.showFlagInput = false
    },

    // Cancel in edit modal: close without saving
    cancelEdit () {
      this.showEditModal = false
      this.newFlagText = ''
      this.showFlagInput = false
    },

    // Click Save in edit modal: open confirm modal
    onSaveClicked () {
      this.showSaveConfirmModal = true
    },

    // Confirm save: update existing or add new agent
    async confirmSaveAgent () {
      if (this.isAddMode) {
        // Call Cloud Function to save agent configuration
        try {
          const agentData = {
            agentName: this.editForm.name,
            apiKey: this.editForm.apiKey,
            customFlags: this.editForm.flags,
            sensitivityProfile: this.editForm.profile,
            ageGroup: this.editForm.ageGroup,
            additionalInstructions: this.editForm.additionalPrompt,
            clientId: this.clientId
          };

          // Use Firebase Auth UID for consistency
          const userId = this.$store.state.authId || window.localStorage.getItem('userId') || this.clientId;
          console.log('🔍 [AiModeration] Saving agent with userId:', userId);
          const result = await saveAgentSettings(
            userId,
            agentData.apiKey,
            agentData.sensitivityProfile,
            agentData.customFlags,
            agentData.ageGroup,
            agentData.additionalInstructions
          );

          if (result.success) {
            // Reload agent from client-side
            const loadResult = await getAgentSettings(userId);
            if (loadResult.success && loadResult.data) {
              const agent = loadResult.data;
              this.agents = [{
                id: userId,
                name: agentData.agentName || 'My AI Agent',
                flags: agent.custom_flags || [],
                profile: agent.sensitivity_profile || 'Balanced',
                ageGroup: agent.age_group || '13+',
                surveyFlagged: (agent.surveys_flagged || 0).toString(),
                apiCost: 'Free (Gemini)',
                totalUsage: (agent.usage_count || 0).toString(),
                additionalPrompt: agent.additional_instructions || ''
              }];
            }

            this.$bvToast.toast('✅ AI Agent saved successfully!', {
              title: 'Success',
              variant: 'success',
              solid: true
            });
          } else {
            throw new Error(result.error);
          }
        } catch (error) {
          console.error('Failed to save agent:', error);
          this.$bvToast.toast(error.error || 'Failed to save agent', {
            title: 'Error',
            variant: 'danger',
            solid: true
          });
        }
      } else {
        // Update existing agent - save to backend
        try {
          const userId = this.editForm.id;
          const result = await saveAgentSettings(
            userId,
            this.editForm.apiKey || '', // If empty, keep existing
            this.editForm.profile,
            this.editForm.flags,
            this.editForm.ageGroup,
            this.editForm.additionalPrompt
          );

          if (result.success) {
            // Reload from backend
            const loadResult = await getAgentSettings(userId);
            if (loadResult.success && loadResult.data) {
              const agent = loadResult.data;
              this.agents = [{
                id: userId,
                name: this.editForm.name || 'My AI Agent',
                flags: agent.custom_flags || [],
                profile: agent.sensitivity_profile || 'Balanced',
                ageGroup: agent.age_group || '13+',
                surveyFlagged: (agent.surveys_flagged || 0).toString(),
                apiCost: 'Free (Gemini)',
                totalUsage: (agent.usage_count || 0).toString(),
                additionalPrompt: agent.additional_instructions || ''
              }];
            }

            this.$bvToast.toast('✅ AI Agent updated successfully!', {
              title: 'Success',
              variant: 'success',
              solid: true
            });
          } else {
            throw new Error(result.error);
          }
        } catch (error) {
          console.error('Failed to update agent:', error);
          this.$bvToast.toast('❌ ' + (error.message || 'Failed to update agent'), {
            title: 'Error',
            variant: 'danger',
            solid: true
          });
        }
      }

      this.showSaveConfirmModal = false
      this.showEditModal = false
      this.showFlagInput = false
    },

    // When orange plus clicked, show input row and focus it
    openFlagInput () {
      this.showFlagInput = true
      this.$nextTick(() => {
        if (this.$refs.flagInput) {
          this.$refs.flagInput.focus()
        }
      })
    },

    // Add a manually typed flag
    addFlag () {
      const text = this.newFlagText.trim()
      if (!text) return
      if (!this.editForm.flags.includes(text)) {
        this.editForm.flags.push(text)
      }
      this.newFlagText = ''
      this.showFlagInput = false
    },

    // Remove a flag by index
    removeFlag (index) {
      this.editForm.flags.splice(index, 1)
    },

    // Open delete confirmation modal
    openDeleteConfirm (agent) {
      this.agentToDelete = agent
      this.showDeleteConfirmModal = true
    },

    // Confirm delete: remove from agents list
    async confirmDeleteAgent () {
      if (this.agentToDelete) {
        try {
          const result = await AIAgentService.deleteAgentConfig(this.agentToDelete.id);

          if (result.success) {
            // Remove from local list
            this.agents = this.agents.filter(a => a.id !== this.agentToDelete.id);

            this.$bvToast.toast('Agent deleted successfully', {
              title: 'Success',
              variant: 'success',
              solid: true
            });
          }
        } catch (error) {
          console.error('Failed to delete agent:', error);
          this.$bvToast.toast(error.error || 'Failed to delete agent', {
            title: 'Error',
            variant: 'danger',
            solid: true
          });
        }
      }
      this.agentToDelete = null
      this.showDeleteConfirmModal = false
    }
  }
}
</script>

<style scoped>

/* Overall layout: left nav + right page */

.ai-moderation-wrapper {
  width: 100%;
}

.ai-agent-page {
  display: flex;
  flex-direction: column;
  min-height: 100vh;
  width: 100%;
  background-color: #eeeeee;
}

/* header & toggle */

.ai-agent-header {
  position: relative;
  background-color: #ffffff;
  display: flex;
  align-items: center;
  justify-content: center;
  height: 75px;
  box-shadow: 0 4px 10px -6px grey;
}

.page-title {
  margin: 0;
  font-size: 26px;
  font-weight: 700;
  color: #ff7222;
}

.moderation-toggle-wrapper {
  position: absolute;
  right: 40px;
  display: flex;
  align-items: center;
  gap: 10px;
  font-size: 14px;
}

.toggle-label {
  font-weight: 500;
  color: #555555;
}


/* Toggle: solid red when checked */

.toggle-switch {
  position: relative;
  display: inline-block;
  width: 54px;
  height: 28px;
}

.toggle-switch input {
  opacity: 0;
  width: 0;
  height: 0;
}

.toggle-track {
  position: absolute;
  inset: 0;
  background-color: #dddddd;
  border-radius: 999px;
  transition: background-color 0.2s ease;
  display: flex;
  align-items: center;
}

.toggle-knob {
  width: 22px;
  height: 22px;
  margin-left: 3px;
  border-radius: 50%;
  background-color: #ffffff;
  box-shadow: 0 1px 4px rgba(0, 0, 0, 0.25);
  transition: transform 0.2s ease;
}

/* checked status */
.toggle-switch input:checked + .toggle-track {
  background-color: #db3360;
}

.toggle-switch input:checked + .toggle-track .toggle-knob {
  transform: translateX(23px);
}

/* content */

.ai-agent-content {
  flex: 1;
  display: flex;
  flex-direction: column;
  padding: 24px 40px 32px;
  box-sizing: border-box;
}


.agent-card-grid {
  display: flex;
  flex-wrap: wrap;
  gap: 24px;
  align-items: flex-start;
}

/* When moderation is off, dim and disable content */
.content-disabled {
  opacity: 0.4;
  pointer-events: none;
}

.agent-card {
  width: 380px;
  min-height: 320px;
  background-color: #ffffff;
  border-radius: 30px;
  padding: 16px 18px 18px;
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.12);
  display: flex;
  flex-direction: column;
}


.agent-card-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  margin-bottom: 8px;
}

/* Status dot: pink border; solid red when active */
.status-dot {
  width: 18px;
  height: 18px;
  border-radius: 50%;
  border: 3px solid #ff8fab;
  background-color: #ffffff;
  padding: 0;
  margin: 0;
  outline: none;
  cursor: pointer;
}

.status-dot.active {
  border-color: #db3360;
  background-color: #db3360;
}

.agent-name {
  font-size: 16px;
  font-weight: 600;
  margin: 0;
  flex: 1;
}

.agent-actions {
  display: flex;
  gap: 8px;
}

/* Edit / Delete / bottom btn */

.btn-pill {
  border-radius: 999px;
  padding: 4px 16px;
  font-size: 14px;
  font-weight: 600;
  border: 2px solid transparent;
  cursor: pointer;
}

.btn-edit {
  background-color: #ffffff;
  color: #db3360;
  border-color: #db3360;
}

.btn-delete {
  background-color: #db3360;
  color: #ffffff;
}

.agent-section {
  display: flex;
  flex-direction: column;
  gap: 4px;
  margin-bottom: 4px;
}

.section-label {
  font-size: 12px;
  font-weight: 600;
  color: #555555;
}

/* Flag / meta chips */

.flag-chips {
  display: flex;
  flex-wrap: wrap;
  gap: 4px;
}

.chip {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 4px 12px;
  border-radius: 999px;
  font-size: 12px;
  font-weight: 500;
  white-space: nowrap;
}

.chip--flag,
.chip--meta {
  background-color: #ff7222;
  color: #ffffff;
}


.agent-meta {
  display: flex;
  gap: 12px;
}

/* statistic rows */

.agent-stats-row {
  margin-top: 8px;
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 8px;
}

.stat-pill {
  background-color: #f5f5f5;
  border-radius: 18px;
  padding: 6px 8px;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 2px;
}

.stat-label {
  font-size: 11px;
  color: #777777;
}

.stat-value {
  font-size: 13px;
  font-weight: 600;
  color: #ff7222;
}

.agent-footer {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-top: auto;
  padding-top: 18px;
}

.footer-btn {
  min-width: 80px;
  padding: 6px 24px;
  border-radius: 999px;
  border: none;
  background-color: #db3360;
  color: #ffffff;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
}

.pagination {
  flex: 1;
  display: flex;
  justify-content: center;
}

.page-pill {
  border: none;
  background-color: #ff7222;
  color: #ffffff;
  border-radius: 999px;
  padding: 6px 16px;
  font-size: 14px;
  font-weight: 600;
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.12);
  cursor: pointer;
}

/* Edit / Confirm pop up */

.modal-header {
  align-items: center;
}

.modal-header button {
  margin-left: auto;
}

.edit-modal-body {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.edit-section {
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.edit-label {
  font-size: 14px;
  font-weight: 600;
  color: #333333;
}

.edit-input {
  border-radius: 8px;
  border: 1px solid #cccccc;
  padding: 6px 10px;
  font-size: 14px;
  width: 100%;
}

.edit-textarea {
  border-radius: 8px;
  border: 1px solid #cccccc;
  padding: 6px 10px;
  font-size: 14px;
  width: 100%;
  resize: vertical;
}

/* Flags in modal */

.edit-flag-list {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
}

.edit-flag-pill {
  display: inline-flex;
  align-items: center;
  padding: 4px 8px;
  border-radius: 999px;
  background-color: #ff7222;
  color: #ffffff;
  font-size: 12px;
}

.edit-flag-remove {
  border: none;
  background-color: transparent;
  color: #ffffff;
  margin-left: 4px;
  cursor: pointer;
  font-weight: 700;
}

.edit-flag-add-pill {
  border: none;
  padding: 4px 10px;
  border-radius: 999px;
  background-color: #ff7222;
  color: #ffffff;
  font-size: 16px;
  font-weight: 700;
  cursor: pointer;
}

.edit-flag-input-row {
  display: flex;
  gap: 8px;
  margin-top: 4px;
}

.small-btn {
  padding: 4px 12px;
  border-radius: 999px;
  border: none;
  background-color: #db3360;
  color: #ffffff;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
}

.small-btn:disabled {
  opacity: 0.5;
  cursor: default;
}


.edit-modal-footer,
.confirm-footer {
  display: flex;
  justify-content: flex-end;
  gap: 8px;
  margin-top: 14px;
}

.modal-btn {
  padding: 6px 18px;
  border-radius: 999px;
  border: none;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
}

.modal-btn-primary {
  background-color: #db3360;
  color: #ffffff;
}

.modal-btn-secondary {
  background-color: #e0e0e0;
  color: #333333;
}

.confirm-text {
  margin: 0;
}
</style>








// WEBPACK FOOTER //
// src/components/admin-only/AiModeration.vue